<template>
  <div class="mt-2">
    <div :style="containerStyle" class="overflow-auto" ref="container">
      <canvas ref="canvasForPanel"></canvas>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Flatten from '@flatten-js/core'
import drawHelpers from '@/api/training/defaults/draw_helpers'
import defaultDrawing from '@/api/training/defaults/default_drawing'
import defaultActions from '@/api/training/defaults/default_actions'
import defaultFunctions from '@/api/training/defaults/default_functions'
import defaultMessages from '@/api/training/defaults/default_messages'
import geometry from '@/api/training/defaults/geometry'
import Overlap from '@/api/training/overlap'
import Gif from '@/api/training/gif'

/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */

export default {
  name: 'TrainingPanel',
  props: {
    panelId: {
      type: Number,
      default() {
        return null
      }
    },
    dummyPanel: {
      type: Object,
      default() {
        return null
      }
    },
    editMode: {
      type: Boolean,
      default() {
        return false
      }
    }
  },
  watch: {
    panelId() {
      this.initPanel()
    },
    editMode() {
      this.setOverlap()
      if (this.editMode === false && this.editItem) {
        this.editItem = null
        this.drawAll()
      }
    }
  },
  data() {
    return {
      loadedCount: 0,
      mousePressed: false,
      xBefore: 0,
      yBefore: 0,
      paramsObject: {
        rotation: 'right'
      },
      canvas: null,
      lastAction: {
        item: null,
        zone: null
      },
      isNeedUpdateStateBefore: true,
      api: null,
      editItem: null,
      overlap: null,
      editGuide: null,
      containerStyle: {
        'max-width': '100%'
      }
    }
  },
  mounted() {
    this.canvas = this.$refs.canvasForPanel
    this.context = this.canvas.getContext('2d')
    this.canvas.addEventListener('mouseup', () => {
      this.onMouseUp()
    })
    this.canvas.addEventListener('mousemove', (e) => {
      this.onMouseMove(e)
    })
    this.canvas.addEventListener('mousedown', (e) => {
      this.onMouseDown(e)
    })

    this.initPanel()
  },
  beforeDestroy() {
    this.panels.forEach((panel) => {
      panel.items.forEach((item) => {
        if (item.gif) {
          item.gif.running = false
        }
      })
    })
  },
  methods: {
    initPanel() {
      this.setCanvasSize()
      this.canvas.style.border = '1px solid black'
      this.api = {
        canvas: this.canvas,
        context: this.context,
        panel: this.currentPanel,
        panels: this.panels,
        resources: this.resources,
        elements: this.elements,
        defaults: {
          actions: defaultActions,
          drawing: defaultDrawing,
          funcs: defaultFunctions,
          msg: defaultMessages
        },
        getActionApi: this.getActionApi,
        panelComponent: this,
        drawHelpers,
        Flatten,
        geometry,
        Gif
      }
      this.editItem = null
      this.editGuide = null
      this.setOverlap()
      this.activeItems = this.currentPanel.items
        .filter(
          (item) => this.elements.itemsById[item.itemId].activeZones !== undefined,
        )
        .reverse()
      this.getCanvasScale()
      this.drawAll()
    },
    setOverlap() {
      this.overlap = new Overlap(
        this.currentPanel.items,
        this.elements.itemsById,
        this.editMode ? 1.5 : 1,
      )
    },
    setCanvasSize() {
      const { sizes } = this.currentPanel
      if (sizes && sizes.canvas) {
        this.canvas.width = sizes.canvas.w
        this.canvas.height = sizes.canvas.h
      } else {
        this.canvas.width = 800
        this.canvas.height = 600
      }
      if (sizes && sizes.container) {
        this.containerStyle = {
          'max-width': `${sizes.container.w}px`,
          'max-height': `${sizes.container.h}px`
        }
      } else {
        window.scrollTo(0, 0)
        const element = this.$refs.container
        const viewport = element.getBoundingClientRect()
        const height = window.innerHeight - viewport.top - 20
        this.containerStyle = {
          height: `${height}px`
        }
      }
    },
    getCanvasScale() {
      this.rect = this.canvas.getBoundingClientRect()
      this.canvasScale = {
        x: this.canvas.width / this.rect.width,
        y: this.canvas.height / this.rect.height
      }
    },
    drawAll() {
      drawHelpers.resetTransform(this.context)
      this.drawBackround()
      this.drawItems()
    },
    drawBackround() {
      drawHelpers.redrawBackground(
        this.context,
        {
          x: 0,
          y: 0,
          w: this.canvas.width,
          h: this.canvas.height
        },
        this.api,
      )
    },
    drawItems() {
      this.currentPanel.items.forEach((item) => {
        this.drawItem(item)
      })
    },
    drawItem(item) {
      const element = this.elements.itemsById[item.itemId]
      this.elements.module[element.displayFunction](
        this.context,
        element,
        item,
        this.api,
      )
    },
    redrawItem(itemToRedraw) {
      const items = this.overlap.orderedClasses[itemToRedraw.id].map(
        (itemId) => this.api.panel.itemsById[itemId],
      )
      items.forEach((item) => {
        drawHelpers.redrawBackground(
          this.context,
          this.overlap.redrawCoordsById[item.id],
          this.api,
        )
      })
      items.forEach((item) => {
        this.drawItem(item)
      })
    },
    onMouseUp() {
      if (this.editMode) {
        this.drawAll()
        this.drawGuides()
        this.editGuide = null
      }
      if (this.lastAction.item) {
        const element = this.elements.itemsById[this.lastAction.item.itemId]
        this.$emit(
          'actionDone',
          this.lastAction.item,
          element,
          this.lastAction.zone,
          this.getActionApi(this.lastAction.item, element),
        )
      }
      this.isNeedUpdateStateBefore = true
      this.mousePressed = false
      this.lastAction.item = null
      this.lastAction.zone = null
      this.yBefore = 0
      this.xBefore = 0
    },
    getItemUnderCoords(x, y, all) {
      let retItem
      let retCoords
      const arr = all ? this.currentPanel.items : this.activeItems
      arr.some((item) => {
        const coords = geometry.reverseTransformPoint(
          { x, y },
          item.coords,
          item.rotation,
        )
        if (geometry.isPointInCoords(coords)) {
          retItem = item
          retCoords = coords
          return true
        }
        return false
      })
      return {
        item: retItem,
        coords: retCoords
      }
    },
    getActiveZoneUnderCoords(coords, element, event) {
      return element.activeZones && element.activeZones.find(
        (zone) => zone.events.includes(event)
          && geometry.isPointInCoords(coords, zone.coords),
      )
    },
    getActionApi(item) {
      return {
        ...this.api,
        action: {
          redrawElement: () => {
            this.redrawItem(item)
          },
          redrawItem: (it) => {
            this.redrawItem(it)
          },
          redrawAll: () => {
            this.drawAll()
          },
          updateCoords: () => {
            this.setOverlap()
          }
        }
      }
    },
    getClickCoords(e) {
      return {
        x: (e.clientX - this.rect.left) * this.canvasScale.x,
        y: (e.clientY - this.rect.top) * this.canvasScale.y
      }
    },
    onAction(e, event) {
      const { x, y } = this.getClickCoords(e)
      const { item, coords } = this.getItemUnderCoords(x, y, this.editMode)
      if (!item) {
        return {
          x,
          y,
          item: null,
          coords: null,
          element: null,
          zone: null
        }
      }

      const element = this.elements.itemsById[item.itemId]
      const zone = this.getActiveZoneUnderCoords(coords, element, event)
      return {
        x,
        y,
        item,
        coords,
        element,
        zone
      }
    },
    onMouseMove(e) {
      if (!this.mousePressed) {
        return
      }
      if (this.editMode) {
        this.dispatchEditAction(e)
        return
      }
      const {
        x, y, item, coords, element, zone
      } = this.onAction(
        e,
        'mousemove',
      )

      if (!zone) {
        return
      }

      this.setParamsObject({ x, y }, coords, item, zone)
      this.updateStateBefore(item)
      this.elements.module[zone.function](
        element,
        item,
        this.context,
        this.getActionApi(item, element),
        this.paramsObject,
      )

      this.lastAction = { item, zone }
      this.xBefore = x
      this.yBefore = y
    },
    onMouseDown(e) {
      this.getCanvasScale()
      this.mousePressed = true
      const {
        x, y, coords, item, element, zone
      } = this.onAction(
        e,
        'mousedown',
      )

      if (this.editMode && (item || this.editItem)) {
        this.toggleEditItem(item, { x, y })
        return
      }

      if (!zone) {
        return
      }

      this.updateStateBefore(item)
      this.setParamsObject({ x, y }, coords, item, zone)
      this.elements.module[zone.function](
        element,
        item,
        this.context,
        this.getActionApi(item, element),
        this.paramsObject,
      )

      this.lastAction = { item, zone }
      this.xBefore = x
      this.yBefore = y
    },
    toggleEditItem(item, clickCoords) {
      if (this.editItem !== null) {
        const guides = geometry.getGuides(this.editItem)
        const guide = guides.find((g) => geometry.isPointInCoords(clickCoords, g))
        if (guide) {
          this.editGuide = guides.indexOf(guide)
          return
        }
      }
      if (this.editItem === null) {
        this.editItem = item
        if (!this.editItem.rotation) {
          this.editItem.rotation = { angle: 0 }
        }
        if (!this.editItem.zIndex) {
          this.editItem.zIndex = 0
        }
      } else {
        this.editItem = null
      }
      this.redrawItem(item)
      this.drawGuides()
    },
    drawGuides() {
      if (this.editItem) {
        defaultDrawing._drawGuides(this.context, null, this.editItem, this.api)
      }
    },
    getEditApi(item) {
      return {
        ...this.api,
        action: {
          redrawElement: () => {
            this.redrawItem(item)
            this.drawGuides()
          },
          updateCoords: () => {
            this.setOverlap()
          }
        }
      }
    },
    dispatchEditAction(e) {
      if (!this.editItem) {
        return
      }

      const { x, y } = this.getClickCoords(e)
      const coords = geometry.reverseTransformPoint(
        { x, y },
        this.editItem.coords,
        this.editItem.rotation,
      )
      const element = this.elements.itemsById[this.editItem.itemId]
      this.setParamsObject({ x, y }, coords, this.editItem, {
        coords: {
          x: 0,
          y: 0,
          h: 100,
          w: 100
        }
      })
      this.paramsObject = { ...this.paramsObject, editGuide: this.editGuide }
      if (this.editGuide === null) {
        return
      }
      defaultActions._moveGuide(
        element,
        this.editItem,
        this.context,
        this.getEditApi(this.editItem, element),
        this.paramsObject,
      )
      this.$emit('change')
    },
    updateStateBefore() {
      if (this.isNeedUpdateStateBefore && this.currentPanel.statesBefore) {
        const step = this.currentStep
        this.currentPanel.statesBefore[step] = this.currentPanel.save()
        this.isNeedUpdateStateBefore = false
      }
    },
    getParamsObject(clickCoords, coords, item, zone) {
      const paramsObject = {}
      paramsObject.clickCoords = clickCoords
      paramsObject.coords = coords
      if (!zone) {
        paramsObject.rotation = null
        return paramsObject
      }
      const zoneCenter = Flatten.point(
        zone.coords.w / 2,
        zone.coords.h / 2,
      ).translate(zone.coords.x, zone.coords.y)
      const vector = Flatten.vector(
        zoneCenter,
        Flatten.point(coords.x, coords.y),
      )
      const clickVector = Flatten.vector(
        Flatten.point(clickCoords.x, clickCoords.y),
        Flatten.point(this.xBefore, this.yBefore),
      )
      if (clickVector.length > 0) {
        if (vector.cross(clickVector) < 0) {
          paramsObject.rotation = 'right'
        } else {
          paramsObject.rotation = 'left'
        }
      } else {
        paramsObject.rotation = null
      }
      return paramsObject
    },
    setParamsObject(...args) {
      this.paramsObject = { ...this.paramsObject, ...this.getParamsObject(...args) }
    },
    setTool(tool) {
      if (!tool) {
        return
      }
      const cursorImage = this.resources.imagesById[tool.textureId]
      this.canvas.style.cursor = `url(${cursorImage.src}), pointer`
    }
  },
  computed: {
    ...mapGetters({
      panels: 'training/getPanels',
      trContant: 'training/getTrainingContent',
      resources: 'training/getResources',
      elements: 'training/getElements',
      currentStep: 'training/getCurrentStep'
    }),
    currentPanel() {
      if (this.dummyPanel) {
        return this.dummyPanel
      }
      if (Number.isInteger(this.panelId)) {
        return this.panels.find((p) => p.id === Number(this.panelId))
      }
      return this.panels.find(
        (p) => p.id === Number(this.$route.params.panelId),
      )
    }
  }
}
</script>

<style scoped>
.panelDiv__panel-canvas {
  width: 100%;
  height: 100%;
}
</style>
